import React, { ReactNode } from 'react';

export interface GenericCardProps {
  children: ReactNode;
  border?: boolean;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
}

interface HeaderProps {
  children: ReactNode;
  icon?: ReactNode;
  subtitle?: string
  titleAside?: ReactNode;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
}

interface BodyProps {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
}

interface FooterProps {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
}

const Header: React.FC<HeaderProps> = ({ children, icon, titleAside, className, onClick, disabled }) => (
  <>{children}</>
);

const Body: React.FC<BodyProps> = ({ children, className, onClick, disabled }) => (
  <>{children}</>
);

const Footer: React.FC<FooterProps> = ({ children, className, onClick, disabled }) => (
  <>{children}</>
);

export const GenericCard: React.FC<GenericCardProps> & {
  Header: typeof Header;
  Body: typeof Body;
  Footer: typeof Footer;
} = ({ children, border, className = "", onClick, disabled = false }) => {
  // Check if there is a Body component among children
  const hasBodyComponent = React.Children.toArray(children).some(
    child => React.isValidElement(child) && child.type === Body
  );

  const renderedChildren = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;

    if (child.type === Header) {
      const {
        className: headerClassName = "",
        onClick: headerOnClick,
        disabled: headerDisabled = false,
        icon,
        titleAside,
        subtitle,
        children: headerChildren
      } = child.props;

      const isDisabled = disabled || headerDisabled;

      const handleClick = isDisabled
        ? (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
        }
        : headerOnClick;

      // Classes conditionnelles
      const clickableStyle = !isDisabled && headerOnClick ? "cursor-pointer" : "";
      const disabledStyle = isDisabled ? "opacity-50" : "";
      // Remove border-b when there's no Body component
      const borderStyle = hasBodyComponent ? "border-b" : "";

      // Add height styles if there's no body component
      const heightStyle = !hasBodyComponent ? "h-full" : "";

      return (
        <div
          className={`p-3 ${borderStyle} flex items-center justify-between gap-3 ${heightStyle} ${headerClassName} ${clickableStyle} ${disabledStyle}`}
          onClick={handleClick}
          aria-disabled={isDisabled}
        >

          <div className="flex items-center gap-4">
            {icon && icon}
            <div>
              <div>{headerChildren}</div>
              {
                subtitle &&
                <div className="flex items-center gap-4">
                  <div className="text-zinc-500 text-sm">{subtitle}</div>
                </div>
              }
            </div>
          </div>
          {titleAside && (
            <div className="flex items-center">
              {titleAside}
            </div>
          )}
        </div>
      );
    }

    if (child.type === Body) {
      const {
        className: bodyClassName = "",
        onClick: bodyOnClick,
        disabled: bodyDisabled = false
      } = child.props;

      const isDisabled = disabled || bodyDisabled;

      const handleClick = isDisabled
        ? (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
        }
        : bodyOnClick;

      // Classes conditionnelles
      const clickableStyle = !isDisabled && bodyOnClick ? "cursor-pointer" : "";
      const disabledStyle = isDisabled ? "opacity-50 cursor-not-allowed" : "";

      return (
        <div
          className={`p-3 ${bodyClassName} ${clickableStyle} ${disabledStyle}`}
          onClick={handleClick}
          aria-disabled={isDisabled}
        >
          {child.props.children}
        </div>
      );
    }

    if (child.type === Footer) {
      const {
        className: footerClassName = "",
        onClick: footerOnClick,
        disabled: footerDisabled = false
      } = child.props;

      const isDisabled = disabled || footerDisabled;

      const handleClick = isDisabled
        ? (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
        }
        : footerOnClick;

      // Classes conditionnelles
      const clickableStyle = !isDisabled && footerOnClick ? "cursor-pointer" : "";
      const disabledStyle = isDisabled ? "opacity-50 cursor-not-allowed" : "";

      return (
        <div
          className={`p-3 border-t flex justify-end gap-3 ${footerClassName} ${clickableStyle} ${disabledStyle}`}
          onClick={handleClick}
          aria-disabled={isDisabled}
        >
          {child.props.children}
        </div>
      );
    }

    return child;
  });

  // Classes conditionnelles pour le composant principal
  const clickableStyle = !disabled && onClick ? "cursor-pointer" : "";
  const disabledStyle = disabled ? "opacity-50 cursor-not-allowed" : "";

  const handleCardClick = disabled
    ? (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    }
    : onClick;

  // Add flex and h-full to the main container if there's no body
  const cardHeightStyle = !hasBodyComponent ? "flex flex-col h-full" : "";

  return (
    <div
      className={`rounded-xl bg-white w-full border ${border && !disabled && "hover:shadow"} ${cardHeightStyle} ${className} ${clickableStyle} ${disabledStyle}`}
      onClick={handleCardClick}
      aria-disabled={disabled}
    >
      {renderedChildren}
    </div>
  );
};

GenericCard.Header = Header;
GenericCard.Body = Body;
GenericCard.Footer = Footer;
